import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import {
  EventInterestOption,
  EventPublicCompetitionData,
  EventRsvpOption,
  GetSuperAdminPendingEventsResponseBody,
  GetUnauthenticatedEventsResponseBody,
  GetUserEventsResponseBody,
  PublicEvent,
  PublicFeedEvent,
} from '@shared/types/event';

import API from '@api/API';

import { useCurrentUserCity, useCurrentUserLocation } from '@hooks/useCurrentUserLocation';

import { CritiqueData, UserCritique } from './CritiqueQueries';
import { useCurrentUser } from './UserQueries';

export type { EventInterestOption, EventRsvpOption };
export type FeedEvent = PublicFeedEvent;

export const useEvents = (
  queryParams: { cityId?: string; startDate?: string; endDate?: string; includeInactive?: 'true' },
  isEnabled = true,
) => {
  const { cityId, startDate, endDate, includeInactive } = queryParams;
  const queryString = new URLSearchParams(queryParams).toString();

  const { isFetched, data, refetch } = useQuery({
    queryKey: ['events', cityId, startDate, endDate, includeInactive],
    queryFn: () => API.get<{ events: any[] }>(`/v1/events?${queryString}`),
    enabled: isEnabled,
  });

  return {
    eventsAreReady: isFetched,
    events: data?.events,
    refetchEvents: refetch,
  };
};

export const useUserEventsQuery = (
  options: { ignoreLocation?: boolean; refetchOnWindowFocus?: boolean | 'always' } = {},
) => {
  const { currentUser } = useCurrentUser();
  const { currentLocation } = useCurrentUserLocation({
    context: 'useUserEventsQuery',
    forceDisable: !currentUser?._id || options.ignoreLocation,
  });
  const { currentCity } = useCurrentUserCity();
  const { lat, lng } = currentLocation ?? {};
  const { refetchOnWindowFocus } = options;

  const {
    isFetched: eventsAreReady,
    data = {
      events: [],
      suggested: [],
      saved: [],
      going: [],
      maybe: [],
      notGoing: [],
      interested: [],
    },
    refetch,
  } = useQuery({
    queryKey: ['events', currentCity?._id, currentUser?._id],
    queryFn: () =>
      currentUser?._id
        ? API.get<GetUserEventsResponseBody>(
            options.ignoreLocation
              ? `/v1/user/events`
              : `/v1/user/events?lat=${lat}&lng=${lng}&page=0&limit=50`,
          )
        : Promise.resolve({
            events: [],
            suggested: [],
            saved: [],
            going: [],
            maybe: [],
            notGoing: [],
            interested: [],
          }),
    enabled: !!currentUser?._id,
    refetchOnWindowFocus,
  });

  return {
    eventsAreReady: !!currentUser?._id ? eventsAreReady : true,
    events: data,
    refetchEvents: refetch,
  };
};

export const useEventQuery = (
  eventId: string,
  options: {
    onSuccess?: (data: { event: PublicEvent; referralUrl?: string; inviteUrl?: string }) => void;
  } = {},
) => {
  const [didInitialFetch, setDidInitialFetch] = useState(false);
  const { currentUser } = useCurrentUser();

  const {
    isFetched: eventIsReady,
    data,
    refetch,
  } = useQuery({
    queryKey: ['event', eventId, currentUser?._id ?? 'unauthenticated'],
    queryFn: async () => {
      const data = await useEventQuery.fn(eventId);
      options.onSuccess?.(data);
      setDidInitialFetch(true);
      return data;
    },
    enabled: !!eventId,
  });

  return {
    didInitialEventFetch: didInitialFetch,
    eventIsReady,
    event: data?.event,
    entertainerEventReferralUrl: data?.referralUrl,
    userEventInviteUrl: data?.inviteUrl,
    refetchEvent: refetch,
  };
};

useEventQuery.fn = (eventId: string) =>
  API.get<{ event: PublicEvent; referralUrl?: string; inviteUrl?: string }>(
    `/v1/events/${eventId}`,
  );

export interface UserEventRsvp {
  isStarred?: boolean;
  isLiked?: boolean;
  rsvp?: EventRsvpOption;
  eventInterest?: EventInterestOption;
  hyped?: string[];
  interested?: string[];
  notInterested?: string[];
}

interface UserEventsRsvps {
  rsvps: Record<string, UserEventRsvp>;
}

export const useCurrentUserEventRsvpsQuery = (
  options: { isEnabled?: boolean; onSuccess?: (data: UserEventsRsvps) => void } = {},
) => {
  const { userIsLoggedIn } = useAppContext();
  const { isEnabled = true } = options;

  const { onSuccess } = options;

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserEventRsvpsQuery.queryKey(),
    queryFn: async () => {
      const data = await useCurrentUserEventRsvpsQuery.queryFn();
      onSuccess?.(data);
      return data;
    },
    enabled: isEnabled && userIsLoggedIn,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    eventRsvpsAreReady: isFetched,
    eventRsvps: data?.rsvps,
    refetchEventRsvps: refetch,
  };
};
useCurrentUserEventRsvpsQuery.queryKey = () => ['event-rsvps'];
useCurrentUserEventRsvpsQuery.queryFn = () => API.get<UserEventsRsvps>(`/v1/user/events/rsvps`);

export const useCurrentUserEventRsvp = (
  eventId: string,
  options: { onSuccess?: (data: UserEventRsvp) => void } = {},
) => {
  const { eventRsvpsAreReady, eventRsvps } = useCurrentUserEventRsvpsQuery({
    onSuccess: (data) => options.onSuccess?.(data[eventId]),
  });

  return { rsvp: eventRsvps?.[eventId] };
};

export interface InterestData {
  eventInterest: Record<EventInterestOption, number>;
  reasons: Record<EventInterestOption, { count: number; label: string }[]>;
  reasonTotals: Record<EventInterestOption, number>;
}

export const useEventInterestDataQuery = (eventId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEventInterestDataQuery.queryKey(eventId),
    queryFn: () => useEventInterestDataQuery.queryFn(eventId),
    enabled: isEnabled && !!eventId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    eventInterestDataIsReady: isFetched,
    eventInterestData: data?.interestData,
    refetchEventInterestData: refetch,
  };
};
useEventInterestDataQuery.queryKey = (eventId: string) => ['event-interest', eventId];
useEventInterestDataQuery.queryFn = (eventId: string) =>
  API.get<{ interestData: InterestData }>(`/v1/events/${eventId}/interest`);

export const useEventCritiqueDataQuery = (eventId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEventCritiqueDataQuery.queryKey(eventId),
    queryFn: () => useEventCritiqueDataQuery.queryFn(eventId),
    enabled: isEnabled && !!eventId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    eventCritiqueDataIdReady: isFetched,
    eventCritiqueData: data?.critiqueData,
    refetchEventCritiqueData: refetch,
  };
};
useEventCritiqueDataQuery.queryKey = (eventId: string) => ['event-critiques', eventId];
useEventCritiqueDataQuery.queryFn = (eventId: string) =>
  API.get<{ critiqueData: CritiqueData }>(`/v1/events/${eventId}/critiques`);

export const useEventCompetitionDataQuery = (eventId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEventCompetitionDataQuery.queryKey(eventId),
    queryFn: () => useEventCompetitionDataQuery.queryFn(eventId),
    enabled: isEnabled && !!eventId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    eventCompetitionDataIsReady: isFetched,
    eventCompetitionData: data?.competitionData,
    refetchEventCompetitionData: refetch,
  };
};
useEventCompetitionDataQuery.queryKey = (eventId: string) => ['event-competition-data', eventId];
useEventCompetitionDataQuery.queryFn = (eventId: string) =>
  API.get<{ competitionData: EventPublicCompetitionData }>(
    `/v1/events/${eventId}/competition-data`,
  );

export const useCurrentUserEventCritiqueQuery = (eventId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserEventCritiqueQuery.queryKey(eventId),
    queryFn: () => useCurrentUserEventCritiqueQuery.queryFn(eventId),
    enabled: isEnabled,
  });

  return {
    userEventCritiqueIdReady: isFetched,
    userEventCritique: data?.userCritique,
    refetchUserEventCritique: refetch,
  };
};
useCurrentUserEventCritiqueQuery.queryKey = (eventId: string) => [
  'user-critiques',
  'events',
  eventId,
];
useCurrentUserEventCritiqueQuery.queryFn = (eventId: string) =>
  API.get<{ userCritique: UserCritique }>(`/v1/user/critiques/events/${eventId}`);

export const useUnauthorizedEventsQuery = (
  params: {
    startDate?: string;
    endDate?: string;
    includeInactive?: 'true';
    includeUnapprovedRecurringEvents?: 'true';
    citySlug?: string;
    cityId?: string;
    search?: string;
    tagIds?: string[];
    featured?: 'true';
    rsvpData?: 'mock' | 'calculate' | 'ignore';
    limit?: string;
    forceSupply?: 'true';
  },
  enabled: boolean,
  options?: {
    refetchOnMount?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnWindowFocus?: boolean | 'always';
  },
) => {
  const { tagIds, ...baseParams } = params;
  const queryParams = new URLSearchParams(baseParams);
  tagIds?.forEach((id) => queryParams.append('tagIds', id));

  const { isFetched, data, refetch } = useQuery({
    queryKey: ['embed-events', params],
    queryFn: () =>
      API.get<GetUnauthenticatedEventsResponseBody>(`/v1/embed/events?${queryParams.toString()}`),
    enabled,
    ...(options ?? {}),
  });

  return {
    eventsAreReady: isFetched,
    events: data?.events,
    eventsMeta: data?.meta,
    refetchEvents: refetch,
  };
};

export const useSeriesPendingEvents = (
  seriesId: string,
  {
    enabled,
  }: {
    enabled?: boolean;
  },
) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: ['pending-events', seriesId],
    queryFn: () =>
      API.get<GetUnauthenticatedEventsResponseBody>(`/v1/series/${seriesId}/pending-events`),
    enabled,
  });

  return {
    pendingEventsAreReady: isFetched,
    pendingEvents: data?.events,
    pendingEventsMeta: data?.meta,
    refetchPendingEvents: refetch,
  };
};

export const useSuperAdminPendingEvents = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: ['super-admin', 'pending-events'],
    queryFn: () =>
      API.get<GetSuperAdminPendingEventsResponseBody>(`/v1/super-admin/pending-events`),
    enabled,
  });

  return {
    pendingEventsAreReady: isFetched,
    pendingEvents: data?.events,
    pendingEventsMeta: data?.meta,
    refetchPendingEvents: refetch,
  };
};

export interface EventReview {
  _id: string;
  postedAt: string;
  comment: string;
  isUserLiked: boolean;
  isCurrentUsersComment: boolean;
  likeCount?: number;
  parentReviewId?: string;
}

export interface SeriesEventBonusTipVotes {
  eventVoteCount: number;
  seriesBonusTipVoteCount: number;
  seriesBonusTipAmount: number;
  canUserVote: boolean;
  seriesId: string;
  seriesBonusTipId: string;
}

export const useEventReviewsQuery = (eventId: string, distinctId: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['eventReviews', eventId],
    queryFn: () =>
      API.get<{
        eventReviews: EventReview[];
        seriesEventBonusTipVotes?: SeriesEventBonusTipVotes;
      }>(`/v1/events/${eventId}/reviews?distinctId=${distinctId}`),
    enabled: !!eventId,
  });

  return {
    eventReviews: data?.eventReviews,
    seriesEventBonusTipVotes: data?.seriesEventBonusTipVotes,
    areEventReviewsFetched: isFetched,
    refetchEventReviews: refetch,
  };
};
